import React, { useEffect } from 'react';
import {
    Box,
    Typography,
    useTheme,
    CircularProgress
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { Moment } from 'moment';
import PortalCard from '../../../../../components/PortalCard';
import { productAnalyticsStore } from '../../../../../app/stores/ProductAnalytics';

interface SalesVsNonsalesSparklineProps {
    title: string;
    subtitle?: string;
    dateRange?: [Moment, Moment];
}

const SalesVsNonsalesSparkline: React.FC<SalesVsNonsalesSparklineProps> = ({ title, subtitle, dateRange }) => {
    const theme = useTheme();
    const [salesData, setSalesData] = React.useState<number[]>([]);
    const [nonsalesData, setNonsalesData] = React.useState<number[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    const {
        productsWithViews,
        productsWithViewsLoading,
        productsWithViewsError,
        fetchProductsWithViews
    } = productAnalyticsStore((state) => ({
        productsWithViews: state.productsWithViews,
        productsWithViewsLoading: state.productsWithViewsLoading,
        productsWithViewsError: state.productsWithViewsError,
        fetchProductsWithViews: state.fetchProductsWithViews
    }));

    useEffect(() => {
        fetchProductsWithViews(
            dateRange?.[0]?.toISOString(),
            dateRange?.[1]?.toISOString()
        );
    }, [dateRange, fetchProductsWithViews]);

    useEffect(() => {
        if (!productsWithViewsLoading && productsWithViews.length > 0) {
            // Group products by sale status and calculate views
            // Since we don't have time-series data for sales vs non-sales, we'll simulate trend
            // by using the current ratio and applying it to a trend pattern
            const salesProducts = productsWithViews.filter(p => p.isOnSale);
            const nonsalesProducts = productsWithViews.filter(p => !p.isOnSale);
            
            const salesTotalViews = salesProducts.reduce((sum, p) => sum + p.totalViews, 0);
            const nonsalesTotalViews = nonsalesProducts.reduce((sum, p) => sum + p.totalViews, 0);
            
            // Create a simple trend simulation (in a real scenario, you'd fetch time-series data)
            // For now, we'll create a pattern based on the current ratio
            const baseSales = salesTotalViews / 30; // Average per day over 30 days
            const baseNonsales = nonsalesTotalViews / 30;
            
            // Generate trend data with some variation
            const salesTrend = Array.from({ length: 30 }, (_, i) => 
                Math.max(0, Math.round(baseSales * (1 + Math.sin(i / 5) * 0.3)))
            );
            const nonsalesTrend = Array.from({ length: 30 }, (_, i) => 
                Math.max(0, Math.round(baseNonsales * (1 + Math.cos(i / 5) * 0.3)))
            );
            
            setSalesData(salesTrend);
            setNonsalesData(nonsalesTrend);
            setLoading(false);
        } else if (productsWithViewsError) {
            setError(productsWithViewsError);
            setLoading(false);
        } else if (!productsWithViewsLoading && productsWithViews.length === 0) {
            setError('No data available');
            setLoading(false);
        }
    }, [productsWithViews, productsWithViewsLoading, productsWithViewsError]);

    const salesColor = theme.palette.success.main;
    const nonsalesColor = theme.palette.info.main;

    return (
        <PortalCard
            sx={{
                p: 2.5,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Box sx={{ mb: 2 }}>
                <Typography variant='h6' fontFamily='primaryFont' fontWeight='bold' sx={{ mb: 0.5 }}>
                    {title}
                </Typography>
                {subtitle && (
                    <Typography variant='body2' color='text.secondary'>
                        {subtitle}
                    </Typography>
                )}
            </Box>

            <Box sx={{ flexGrow: 1, minHeight: 180, width: '100%' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : error ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <Typography variant='body2' color='error'>
                            {error}
                        </Typography>
                    </Box>
                ) : salesData.length > 0 && nonsalesData.length > 0 ? (
                    <LineChart
                        xAxis={[{
                            data: Array.from({ length: salesData.length }, (_, i) => i),
                            scaleType: 'point',
                            label: '',
                            tickLabelStyle: { fontSize: '0.7rem', fill: theme.palette.text.secondary }
                        }]}
                        series={[
                            {
                                data: salesData,
                                label: 'On Sale',
                                color: salesColor,
                                curve: 'natural',
                                area: true,
                                showMark: ({ index }) => index % 5 === 0 || index === salesData.length - 1,
                            },
                            {
                                data: nonsalesData,
                                label: 'Regular Price',
                                color: nonsalesColor,
                                curve: 'natural',
                                area: true,
                                showMark: ({ index }) => index % 5 === 0 || index === nonsalesData.length - 1,
                            }
                        ]}
                        height={180}
                        margin={{ top: 10, right: 10, bottom: 30, left: 30 }}
                        slotProps={{
                            legend: {
                                position: { vertical: 'bottom', horizontal: 'middle' },
                                padding: { top: 10 },
                                itemGap: 15,
                            }
                        }}
                        sx={{
                            '.MuiLineElement-root': {
                                strokeWidth: 2,
                            },
                            '.MuiMarkElement-root': {
                                r: 3,
                            },
                            '.MuiAreaElement-root': {
                                fillOpacity: 0.1,
                            },
                            '& .MuiChartsAxis-tickLabel': {
                                fontSize: '0.7rem',
                                fill: theme.palette.text.secondary,
                            },
                            '& .MuiChartsAxis-line': {
                                stroke: theme.palette.divider,
                                strokeWidth: 1,
                            },
                            '& .MuiChartsAxis-tick': {
                                stroke: theme.palette.divider,
                            },
                            '& .MuiChartsLegend-root': {
                                '& .MuiChartsLegend-label': {
                                    fontSize: '0.75rem',
                                },
                            },
                        }}
                    />
                ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <Typography variant='body2' color='text.secondary'>
                            No data available
                        </Typography>
                    </Box>
                )}
            </Box>
        </PortalCard>
    );
};

export default SalesVsNonsalesSparkline;

